/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {BUILD_VERSION, METRICS_VERSION} from './BuildVersion';
import type Configuration from './Configuration';
import createConfigMethods, {ConfigMethods} from './MetricsConfig';
import createProcessingMethods, {INIT_FLUSH_PERIOD, MetricsProcessing} from './MetricsProcessing';
import Metrics, {AdobeMetrics, Application, MetricsState, Record, RecordType, User} from '@exc/metrics';
import PageState from './PageState';
import type {Payload} from './Payload';
import {remove as removeCookie, set as setCookie} from './Cookie';
import type Runtime from './Runtime';
import Transport from './Transport';
import uuidv1 from 'uuid/v1';

type RequestInitExtended = RequestInit & {priority?: 'low' | 'high' | 'auto'};
export type FetchFn = (input: RequestInfo | URL, init?: RequestInitExtended) => Promise<Response>;

const DX_OPTOUT_COOKIE = 'adobe_dx_optout';
const USER_RECORD_TYPE = RecordType.USER.toLowerCase();
const LOGGER_NAME = 'exc.metrics-runtime.AdobeMetricsRuntime';

/**
 * Record a metric object to remote storage.
 *
 * @param {AdobeMetricsRuntime} adobeMetrics Adobe Metrics object.
 * @param {MetricsProcessing} metricsProcessing - Metrics Processing methods.
 * @param {Record} r The metric to be recorded.
 * @param {any} _additionalData The additional data to be recorded or filtered.
 * @returns {Promise} A Promise of the number of metrics sent (if any).
 * Note the return value may be undefined, and should be ignored by the caller.
 */
async function writeFn(
  adobeMetrics: AdobeMetricsRuntime,
  metricsProcessing: MetricsProcessing,
  r: Record,
  _additionalData?: any,
): Promise<number> {
  const {applyPageState, processQueue} = metricsProcessing;
  r.dateNow = r.dateNow || Date.now();

  if (!r.recordType) {
    return Promise.reject(new TypeError('RecordType must be defined'));
  }

  // check if we need to do special handling
  const recordType = r.recordType.toLowerCase();
  if (USER_RECORD_TYPE === recordType) {
    return Promise.reject(
      new TypeError("The User RecordType cannot be store()'d. Read the jsdocs for the User RecordType."),
    );
  }

  r.metricsState = applyPageState(r.metricsState);
  adobeMetrics.queue.push(r);

  // TODO: optimize immediate processing
  return processQueue();
}

/**
 * Configure the Metrics package. These options control the
 * operational behavior.
 *
 * If mode is omitted, it will default to "verbose" for dev
 * environment, and "normal" for stage and prod.
 *
 * The current configuration will be updated as compliant as possible
 * according to the supplied configuration object, and returned. If
 * no configuration object is provided, then the current
 * configuration is validated and returned.
 *
 * @param {AdobeMetricsRuntime} adobeMetrics Adobe Metrics object.
 * @param {ConfigMethods} configMethods Configuration methods.
 * @param {MetricsProcessing} metricsProcessing - Metrics Processing methods.
 * @param {Window} win - Global window object.
 * @param {Configuration} config An optional configuration
 * object for the desired state.
 * @returns {Configuration} The current and validated configuration.
 */
function configureFn(
  adobeMetrics: AdobeMetricsRuntime,
  configMethods: ConfigMethods,
  metricsProcessing: MetricsProcessing,
  win: Window,
  config?: Configuration,
): Configuration {
  const {metricsState, runtime, transport} = adobeMetrics;
  const {updateFlushEvent} = metricsProcessing;
  const {setEnvironment, setMode} = configMethods;

  config && setEnvironment(config.environment);
  // derive mode from environment if mode is not specified and hasn't
  // previously been set
  if (config && !config.mode && !runtime.mode) {
    switch (adobeMetrics.metricsState.environment) {
      case 'stage':
      case 'prod':
        setMode('normal');
        break;
      default:
        setMode('verbose');
        break;
    }
  }
  if (config && config.mode) {
    setMode(config.mode);
  }
  if (config && (config.mode === 'off' || config.mode === 'test')) {
    config.batch = false;
  }
  if (config && config.batch !== undefined && runtime.options.transport.batch !== config.batch) {
    runtime.options.transport.batch = config.batch;
    updateFlushEvent(config);
  }
  (runtime.options.transport as any).maxPayloadSize =
    (config && config.maxPayloadSize) || (runtime.options.transport as any).maxPayloadSize;

  config && config.user && adobeMetrics.setUser(config.user, runtime.version);
  config && config.application && adobeMetrics.setApplication(config.application);

  metricsState.deviceId = config?.deviceId || metricsState.deviceId;
  metricsState.instanceId = config?.instanceId || metricsState.instanceId;
  transport.init();

  try {
    const {localStorage, sessionStorage} = win;
    if (config && sessionStorage) {
      if (config.instanceId) {
        sessionStorage.setItem('adobeMetrics.instanceId', config.instanceId);
      }
      if (config.deviceId && localStorage) {
        localStorage.setItem('adobeMetrics.deviceId', config.deviceId);
      }
    }
  } catch (e) {
    // if sessionStorage isn't supported, device and instance tracking won't
    // be possible the environment is probably nodejs, which does not have
    // local or session storage
  }

  const c = {
    batch: runtime.options.transport.batch,
    deviceId: metricsState.deviceId,
    environment: metricsState.environment,
    instanceId: metricsState.instanceId,
    mode: runtime.mode,
    user: Object.assign({}, adobeMetrics.user),
  };
  // remove user.event and dateNow so that they are not re-recorded
  delete c.user.event;
  delete c.user.dateNow;
  return c;
}

/**
 * Set the opt-out status of the user. Creates a adobe_dx_optout cookie if
 * the argument is true and deletes the cookie if the argument is false.
 *
 * @param {Window} global The global object.
 * @param {boolean} optOut Create or Delete the cookie.
 * @param {string} domain The domain to set the cookie on.
 */
function optOutFn(global: Window & typeof globalThis, optOut: boolean, domain?: string): void {
  optOut ? setCookie(global, DX_OPTOUT_COOKIE, '1', {domain}) : removeCookie(global, DX_OPTOUT_COOKIE);
}

/**
 * An interface that describes the global adobeMetrics object.
 *
 * @typedef AdobeMetricsRuntime
 */
export interface AdobeMetricsRuntime extends AdobeMetrics {
  configure(config?: Configuration): Configuration;
  connections: number;
  guid?: string;
  init: {[key: string]: any};
  metricsState: MetricsState;
  now(): number;
  pageState: PageState;
  queryURL: string;
  sdkVersion: string;
  currentHRef: string | undefined;
  timeOrigin: number;
  readonly transport: Transport;
  readonly payload: Payload;
  readonly runtime: Runtime;
  user?: User;

  // deprecated
  clearUser(version?: string): void;
  setApplication(app: Application): void;
  setEnvironment(env: string): void;
  setUser(user: User, version?: string): void;
}

/**
 * Initialize the global.adobeMetrics object. This function
 * is agnostic wrt browser vs nodejs - in either environment
 * the basic adobeMetrics object will be initialized independent.
 *
 * @param {Window} g - The global object / Window.
 * @returns {AdobeMetricsRuntime} A pointer to the global
 *  adobeMetrics object that has been properly initialized.
 */
// eslint-disable-next-line no-restricted-globals
export function initialize(g: Window & typeof globalThis = global as any): AdobeMetricsRuntime {
  const a: any = (g.adobeMetrics = g.adobeMetrics || {});
  const ms = (a.metricsState = a.metricsState || {});
  /**
   * Abstraction layer for performance.now().
   *
   * Window.performance will always exist in the browsers we support,
   * and it exists in the jsdom test harness.
   *
   * Global.performance may or may not exist in nodejs environments,
   * depending on what the application loads.
   *
   * This abstraction layer checks whether or not the performance object
   * and the now() function on it exists, otherwise picks Date.now() as
   * a sensible default.
   *
   * @returns {number} (global as any).performance.now() or Date.now().
   */
  a.now = (): number => g.performance?.now() || Date.now();
  const _now = Date.now();
  a.connections = a.connections || 0;
  a.currentHRef = a.currentHRef || '';
  a.init = a.init || {};
  a.options = a.options || {
    console: {},
    transport: {
      batch: true,
      flushPeriod: INIT_FLUSH_PERIOD,
      maxPayloadSize: 500000,
    },
  };
  a.pageState = a.pageState || new PageState(a, g.location);
  a.queue = a.queue || [];
  a.sdkVersion = a.sdkVersion || METRICS_VERSION;
  a.timeOrigin =
    a.timeOrigin ||
    (g.performance && (g.performance.timeOrigin || (g.performance.timing && g.performance.timing.navigationStart))) ||
    _now;
  a.user = a.user || {
    dateNow: _now,
    event: 'Initialization',
    recordType: RecordType.USER,
  };
  ms.windowId = ms.windowId || uuidv1();
  ms.environment = ms.environment || 'dev';
  ms.runtimeVersion = BUILD_VERSION;

  if (!a.configure || !a.write) {
    const metrics = new Metrics(LOGGER_NAME, a);
    const metricsProcessing = createProcessingMethods(a, g, metrics);
    const configMethods = createConfigMethods(a, metrics);
    const {clearUser, setApplication, setEnvironment, setUser} = configMethods;
    const {create, log, processQueue} = metricsProcessing;
    a.flush = async (): Promise<number> => await processQueue('flushAPI');
    a.write = (r: Record, _additionalData?: any): Promise<number> => writeFn(a, metricsProcessing, r, _additionalData);
    a.configure = (config?: Configuration): Configuration =>
      configureFn(a, configMethods, metricsProcessing, g, config);
    // deprecated functions
    a.setApplication = setApplication;
    a.setEnvironment = setEnvironment;
    a.clearUser = clearUser;
    a.setUser = setUser;
    // global functions
    a.create = create;
    a.log = log;
  }

  a.optOut = (optOut: boolean, domain?: string): void => optOutFn(g, optOut, domain);
  a.transport = a.transport || new Transport(a, g);
  a.payload = a.transport.payload;
  // runtime
  const runtime = (a.runtime = a.runtime || {});
  runtime.mode = runtime.mode || 'normal';
  runtime.options = runtime.options || {};
  runtime.options.console = runtime.options.console || {
    debug: false,
    error: true,
    info: false,
    metric: false,
    trace: false,
    warn: true,
  };
  runtime.options.timestamp = runtime.options.timestamp || false;
  runtime.options.transport = runtime.options.transport || {
    flushPeriod: INIT_FLUSH_PERIOD,
    maxPayloadSize: 500000,
    transmit: true,
  };

  try {
    const {localStorage, sessionStorage} = g as Window;
    ms.instanceId = ms.instanceId || sessionStorage.getItem('adobeMetrics.instanceId');
    if (!ms.instanceId) {
      ms.instanceId = uuidv1();
      g.sessionStorage.setItem('adobeMetrics.instanceId', a.metricsState.instanceId);
    }

    ms.deviceId = ms.deviceId || localStorage.getItem('adobeMetrics.deviceId');
    if (!ms.deviceId) {
      ms.deviceId = uuidv1();
      localStorage.setItem('adobeMetrics.deviceId', a.metricsState.deviceId);
    }
  } catch (e) {
    // if sessionStorage isn't supported, device and instance tracking
    // won't be possible
    ms.deviceId = ms.instanceId = a.metricsState.windowId;
  }
  a.configure({
    batch: a.runtime.options.transport.batch || true,
    environment: a.metricsState.environment,
    mode: a.runtime.mode,
  });
  return a;
}
